import { useLayoutEffect, useRef } from "react";

export function focusNoteField(
  node: HTMLTextAreaElement,
  options?: { preventScroll?: boolean },
) {
  node.focus({ preventScroll: options?.preventScroll ?? false });
  const end = node.value.length;
  node.setSelectionRange(end, end);
}

/**
 * Radix focuses the close button on open unless this handler runs.
 * preventDefault stops that move, then the note field takes focus immediately.
 */
export function focusNoteTextareaOnOpen(event: Event) {
  const current = event.currentTarget;
  if (!(current instanceof HTMLElement)) return;
  const note = current.querySelector("textarea");
  if (!(note instanceof HTMLTextAreaElement)) return;
  event.preventDefault();
  focusNoteField(note);
}

function typedChar(event: KeyboardEvent): string | null {
  if (event.metaKey || event.ctrlKey || event.altKey || event.isComposing) {
    return null;
  }
  if (event.key === "Enter") return "\n";
  if (event.key.length === 1) return event.key;
  return null;
}

function isOtherTextField(
  target: EventTarget | null,
  note: HTMLTextAreaElement | null,
) {
  if (!(target instanceof HTMLElement) || target === note) return false;
  const field = target.closest(
    "input, textarea, select, [contenteditable='true']",
  );
  return field != null && field !== note;
}

/** The sheet close control. Focus landing here during open is not the user leaving the note. */
export function isSheetClose(target: EventTarget | null) {
  return (
    target instanceof HTMLButtonElement &&
    target.querySelector(":scope > span.sr-only") != null
  );
}

/** Buttons keep the buffer during the desktop scroll; the Notitie button is one of them. */
export function retainDesktopNoteFocus(target: EventTarget | null) {
  return target instanceof HTMLButtonElement;
}

type NoteKeystrokeTarget = {
  getNote: () => HTMLTextAreaElement | null;
  append: (char: string) => void;
  retain: (target: EventTarget | null) => boolean;
};

let current: NoteKeystrokeTarget | null = null;
let armed = false;
let sawNoteFocus = false;
let pending = "";
let onBufferedAppend: (() => void) | null = null;

function disarmNoteKeystrokes() {
  if (!armed) return;
  armed = false;
  document.removeEventListener("keydown", onNoteKeyDown, true);
  document.removeEventListener("focusin", onNoteFocusIn, true);
}

function onNoteKeyDown(event: KeyboardEvent) {
  if (!armed) return;
  const note = current?.getNote() ?? null;
  if (note && document.activeElement === note) return;
  if (isOtherTextField(event.target, note)) {
    disarmNoteKeystrokes();
    return;
  }
  const char = typedChar(event);
  if (char == null) return;
  event.preventDefault();
  event.stopPropagation();
  if (current) current.append(char);
  else pending += char;
  onBufferedAppend?.();
}

function onNoteFocusIn(event: FocusEvent) {
  if (!armed || !current) return;
  const note = current.getNote();
  if (note && event.target === note) {
    sawNoteFocus = true;
    return;
  }
  if (!sawNoteFocus || current.retain(event.target)) return;
  disarmNoteKeystrokes();
}

function bindNoteKeystrokes(target: NoteKeystrokeTarget) {
  const prior = current;
  current = target;
  if (pending) {
    const extra = pending;
    pending = "";
    for (const char of extra) target.append(char);
  }
  return () => {
    if (current === target) current = prior;
  };
}

function armNoteKeystrokes() {
  if (armed) return;
  armed = true;
  sawNoteFocus = false;
  document.addEventListener("keydown", onNoteKeyDown, true);
  document.addEventListener("focusin", onNoteFocusIn, true);
}

function releaseNoteKeystrokesWhenFocused() {
  const note = current?.getNote() ?? null;
  if (note?.isConnected && document.activeElement !== note) {
    focusNoteField(note, { preventScroll: true });
  }
  if (!note?.isConnected || document.activeElement === note) {
    disarmNoteKeystrokes();
  }
}

/**
 * Focus the sheet textarea immediately, and again when its open transition ends.
 * The animationend listener runs once. Keys typed before focus are buffered.
 */
function watchSheetNoteFocus(node: HTMLTextAreaElement): () => void {
  let cancelled = false;
  let innerFrame = 0;
  const focus = () => {
    if (cancelled || !node.isConnected) return;
    focusNoteField(node);
  };
  armNoteKeystrokes();
  focus();
  const dialog = node.closest("[role='dialog']");
  const onEnd = (event: Event) => {
    if (event.target !== dialog) return;
    focus();
    releaseNoteKeystrokesWhenFocused();
  };
  if (dialog) {
    dialog.addEventListener("animationend", onEnd, { once: true });
  }
  const outerFrame = requestAnimationFrame(() => {
    innerFrame = requestAnimationFrame(() => {
      focus();
      if (!dialog) releaseNoteKeystrokesWhenFocused();
    });
  });
  return () => {
    cancelled = true;
    cancelAnimationFrame(outerFrame);
    cancelAnimationFrame(innerFrame);
    if (dialog) dialog.removeEventListener("animationend", onEnd);
    releaseNoteKeystrokesWhenFocused();
  };
}

function fixedChromeInsets() {
  let top = 0;
  let bottom = 0;
  for (const node of document.querySelectorAll("header, nav")) {
    if (!(node instanceof HTMLElement)) continue;
    const style = getComputedStyle(node);
    if (style.position !== "fixed" && style.position !== "sticky") continue;
    const rect = node.getBoundingClientRect();
    if (rect.width < window.innerWidth * 0.6 || rect.height < 8) continue;
    if (rect.top <= 1) top = Math.max(top, rect.bottom);
    if (rect.bottom >= window.innerHeight - 1) {
      bottom = Math.max(bottom, window.innerHeight - rect.top);
    }
  }
  return { top, bottom };
}

function scrollingParent(node: HTMLElement) {
  let parent = node.parentElement;
  while (parent) {
    const style = getComputedStyle(parent);
    if (
      /(auto|scroll)/.test(style.overflowY) &&
      parent.scrollHeight > parent.clientHeight + 1
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return document.scrollingElement instanceof HTMLElement
    ? document.scrollingElement
    : document.documentElement;
}

const FIELD_MARGIN = 8;
/** Room between the composer actions and a fixed bottom nav. */
const ACTION_GAP = 16;

let cachedSafeAreaBottom: number | null = null;

function safeAreaBottom() {
  if (cachedSafeAreaBottom != null) return cachedSafeAreaBottom;
  const probe = document.createElement("div");
  probe.style.position = "fixed";
  probe.style.visibility = "hidden";
  probe.style.paddingBottom = "env(safe-area-inset-bottom)";
  document.body.append(probe);
  cachedSafeAreaBottom =
    Number.parseFloat(getComputedStyle(probe).paddingBottom) || 0;
  probe.remove();
  return cachedSafeAreaBottom;
}

/** Bottom of the composer action row, which sits below the field. */
function actionRowBottom(note: HTMLElement) {
  const buttons = note.closest("form")?.querySelectorAll("button");
  const last = buttons?.item(buttons.length - 1);
  const row = last?.parentElement;
  if (!(row instanceof HTMLElement)) return null;
  return row.getBoundingClientRect().bottom;
}

/**
 * Instant scroll. A smooth scroll is cancelled by the keystrokes that arrive
 * in the same moment as the click, and the field stops under the bottom nav.
 * On a phone the action row below the field keeps ACTION_GAP above that nav.
 */
function revealNote(note: HTMLElement) {
  note.scrollIntoView({
    behavior: "auto",
    block: "center",
    inline: "nearest",
  });
  const { top, bottom } = fixedChromeInsets();
  const rect = note.getBoundingClientRect();
  const viewTop = top + FIELD_MARGIN;
  // Desktop has no bottom bar: keep the field itself in view, as before.
  let edgeBottom = rect.bottom;
  let viewBottom = window.innerHeight - bottom - FIELD_MARGIN;
  if (bottom > 0) {
    edgeBottom = Math.max(edgeBottom, actionRowBottom(note) ?? edgeBottom);
    viewBottom = window.innerHeight - bottom - ACTION_GAP - safeAreaBottom();
  }
  let delta = 0;
  if (rect.top < viewTop) {
    delta = rect.top - viewTop;
  } else if (edgeBottom > viewBottom) {
    delta = Math.min(edgeBottom - viewBottom, rect.top - viewTop);
  }
  if (Math.abs(delta) < 1) return;
  scrollingParent(note).scrollBy({ top: delta, behavior: "auto" });
}

let stopReveal: (() => void) | null = null;

/**
 * Put the caret in the note field before any scroll, then bring that field
 * into view above the fixed header and bottom nav. Typing cannot cancel it:
 * the scroll is instant, and it runs again after buffered keys and after the
 * field grows.
 */
export function openDesktopNote() {
  const timeline = document.getElementById("timeline");
  const note = timeline?.querySelector("textarea");
  armNoteKeystrokes();
  stopReveal?.();

  const target = note instanceof HTMLTextAreaElement ? note : timeline;
  if (note instanceof HTMLTextAreaElement) {
    focusNoteField(note, { preventScroll: true });
  }
  if (!target) return;

  let stopped = false;
  const again = () => {
    if (stopped || !target.isConnected) return;
    revealNote(target);
  };
  const onInput = () => {
    again();
    requestAnimationFrame(again);
  };
  const onTransition = (event: Event) => {
    if (
      event instanceof TransitionEvent &&
      event.propertyName !== "min-height"
    ) {
      return;
    }
    again();
  };
  let timer = 0;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    target.removeEventListener("input", onInput);
    target.removeEventListener("transitionend", onTransition);
    window.clearTimeout(timer);
    if (onBufferedAppend === again) onBufferedAppend = null;
    if (stopReveal === stop) stopReveal = null;
  };
  onBufferedAppend = again;
  target.addEventListener("input", onInput);
  target.addEventListener("transitionend", onTransition);
  again();
  timer = window.setTimeout(() => {
    again();
    stop();
  }, 450);
  stopReveal = stop;
}

/**
 * Buffer printable keys that arrive before the note textarea has focus.
 * Mobile arms this on mount. Desktop arms it from the Notitie click.
 */
export function useNoteKeystrokeBuffer({
  getNote,
  noteText,
  onAppend,
  armOnMount,
  retain,
}: {
  getNote: () => HTMLTextAreaElement | null;
  noteText: string;
  onAppend: (next: string) => void;
  armOnMount: boolean;
  retain: (target: EventTarget | null) => boolean;
}) {
  const draftRef = useRef<string | null>(null);
  if (draftRef.current === noteText) draftRef.current = null;
  const textRef = useRef(noteText);
  textRef.current = draftRef.current ?? noteText;
  const caretAtEnd = useRef(false);
  const getNoteRef = useRef(getNote);
  getNoteRef.current = getNote;
  const onAppendRef = useRef(onAppend);
  onAppendRef.current = onAppend;
  const retainRef = useRef(retain);
  retainRef.current = retain;

  useLayoutEffect(() => {
    const node = getNoteRef.current();
    if (!node || !caretAtEnd.current || document.activeElement !== node) {
      return;
    }
    if (node.value !== textRef.current) return;
    caretAtEnd.current = false;
    const end = node.value.length;
    node.setSelectionRange(end, end);
  }, [noteText]);

  useLayoutEffect(() => {
    const unbind = bindNoteKeystrokes({
      getNote: () => getNoteRef.current(),
      append: (char) => {
        const next = textRef.current + char;
        draftRef.current = next;
        textRef.current = next;
        caretAtEnd.current = true;
        onAppendRef.current(next);
      },
      retain: (target) => retainRef.current(target),
    });
    const stop = () => {
      unbind();
      disarmNoteKeystrokes();
    };
    if (!armOnMount) return stop;
    const node = getNoteRef.current();
    if (!node) return stop;
    const cancel = watchSheetNoteFocus(node);
    return () => {
      cancel();
      stop();
    };
  }, [armOnMount]);
}
